// useNodeMissionTemplateEditor.js

import { useMemo } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';

// Hooks
import useDatabaseMissionsState from 'collection/settings/missions/useDatabaseMissionsState';
import useDatabaseSchemaState from 'collection/hooks/useDatabaseSchemaState';

/**
 * useNodeMissionTemplateEditor
 *
 * Wires Mission template authoring modals for the node-native Missions empty state.
 *
 * @param {object} params - Hook parameters.
 * @param {boolean} params.enabled - Whether the editor state should stay initialized.
 * @param {object|null} params.typeDef - Active node type definition.
 * @param {string} params.typeKey - Active node type key.
 * @param {string|null} params.baseKey - Active workspace key.
 *
 * @returns {object} Mission template editor state and schema metadata.
 */
export default function useNodeMissionTemplateEditor({
  enabled = false,
  typeDef = null,
  typeKey = '',
  baseKey = null,
}) {
  const language = useCurrentLanguage();
  const { getAll, getCount, getRevision } = useNodeTypes();

  const resolvedTypeKey = useMemo(() => {
    return String(typeKey || typeDef?.key || '').trim();
  }, [typeDef?.key, typeKey]);

  const {
    resolvedProperties,
    nodeTypeOptions,
  } = useDatabaseSchemaState({
    typeKey: resolvedTypeKey,
    nodeTypeCount: getCount(),
    nodeTypesRevision: getRevision(),
    getAll,
    language,
  });

  const missionsState = useDatabaseMissionsState({
    activeDatabase: null,
    typeDef,
    isOpen: enabled,
    typeKey: resolvedTypeKey,
    baseKey,
  });

  return {
    ...missionsState,
    nodeTypeOptions,
    resolvedProperties,
    typeLabel: String(typeDef?.label || '').trim(),
  };
}
